import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { auth } from "../lib/api";

/**
 * TermsGateModal — bloqueia o acesso ao CRM até o usuário aceitar a versão
 * vigente dos Termos de Uso. Não renderiza nada se não houver termos
 * cadastrados ou se a versão aceita já for a atual — self-contained, monta
 * uma vez por sessão de tenant logado (App.js) e decide sozinho se aparece.
 */
const TermsGateModal = () => {
    const [terms, setTerms] = useState(null); // { content, version }
    const [needsAccept, setNeedsAccept] = useState(false);
    const [accepting, setAccepting] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [termsRes, me] = await Promise.all([
                    api.get("/public/terms").catch(() => null),
                    api.get("/auth/me").catch(() => null),
                ]);
                if (!alive) return;
                if (!termsRes || !termsRes.version || !termsRes.content) return; // nada cadastrado ainda
                if (!me || me.kind !== "tenant_user") return; // só bloqueia tenant logado
                setTerms(termsRes);
                const accepted = me.user?.accepted_terms_version;
                if (accepted !== termsRes.version) {
                    setNeedsAccept(true);
                    auth.cacheMe(me);
                }
            } catch (_) { /* silencioso — nunca trava o app por causa disso */ }
        })();
        return () => { alive = false; };
    }, []);

    if (!needsAccept || !terms) return null;

    const handleAccept = async () => {
        setAccepting(true);
        try {
            await api.post("/tenant/terms/accept", {});
            setNeedsAccept(false);
            toast.success("Termos aceitos", { description: `Versão ${terms.version} registrada.` });
        } catch (e) {
            toast.error("Falha ao registrar aceite", { description: e?.message || "Tente novamente." });
        } finally {
            setAccepting(false);
        }
    };

    return (
        <div
            data-testid="terms-gate-modal"
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        >
            <div className="w-full max-w-lg rounded-3xl border border-amber-500/20 bg-slate-950 shadow-2xl overflow-hidden">
                <div className="p-6 border-b border-white/5">
                    <div className="h-12 w-12 rounded-2xl bg-amber-500/10 border border-amber-500/25 text-amber-300 flex items-center justify-center mb-4">
                        <i className="fa-solid fa-file-contract text-xl"></i>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">
                        Atualização dos Termos de Uso
                    </div>
                    <h2 className="salon-heading text-2xl text-white mt-1">
                        Nossos Termos de Uso mudaram
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">
                        Para continuar usando o Kinng.app, leia e aceite a versão {terms.version}.
                    </p>
                </div>
                <div className="p-6 max-h-[45vh] overflow-y-auto text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">
                    {terms.content}
                </div>
                <div className="p-6 border-t border-white/5">
                    <button
                        data-testid="terms-gate-accept-btn"
                        onClick={handleAccept}
                        disabled={accepting}
                        className="w-full min-h-[52px] rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold uppercase tracking-[0.15em] text-sm transition-all disabled:opacity-60"
                    >
                        {accepting ? (
                            <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Registrando…</>
                        ) : (
                            "Aceitar e Continuar"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TermsGateModal;
